var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { isCSV, isFormData, isFormUrlEncoded, isGraphQL, isJSON, isPDF, isPlainObject, isText, isXML, } from './contentTypeChecks';
import { json2xml } from './json2xml';
import { stringifyOpenAPI } from './stringifyOpenAPI';
export var codeSampleGenerators = [
    {
        id: 'http',
        label: 'HTTP',
        syntax: 'bash',
        generate: function (_a) {
            var method = _a.method, url = _a.url, _b = _a.headers, headers = _b === void 0 ? {} : _b, body = _a.body;
            var _c = parseHostAndPath(url), host = _c.host, path = _c.path;
            if (body) {
                // if we had a body add a content length header
                var bodyContent = body ? stringifyOpenAPI(body) : '';
                // handle unicode chars with a text encoder
                var encoder = new TextEncoder();
                var bodyString_1 = BodyGenerators.getHTTPBody(body, headers);
                if (bodyString_1) {
                    body = bodyString_1;
                }
                headers = __assign(__assign({}, headers), { 'Content-Length': encoder.encode(bodyContent).length.toString() });
            }
            if (!headers.hasOwnProperty('Accept')) {
                headers.Accept = '*/*';
            }
            var headerString = headers
                ? "".concat(Object.entries(headers)
                    .map(function (_a) {
                    var key = _a[0], value = _a[1];
                    return key.toLowerCase() !== 'host' ? "".concat(key, ": ").concat(value) : '';
                })
                    .join('\n'), "\n")
                : '';
            var bodyString = body ? "\n".concat(body) : '';
            var httpRequest = "".concat(method.toUpperCase(), " ").concat(decodeURI(path), " HTTP/1.1\nHost: ").concat(host, "\n").concat(headerString).concat(bodyString);
            return httpRequest;
        },
    },
    {
        id: 'curl',
        label: 'cURL',
        syntax: 'bash',
        generate: function (_a) {
            var method = _a.method, url = _a.url, headers = _a.headers, body = _a.body;
            var separator = ' \\\n';
            var lines = ['curl -L'];
            if (method.toUpperCase() !== 'GET') {
                lines.push("--request ".concat(method.toUpperCase()));
            }
            lines.push("--url '".concat(url, "'"));
            if (body) {
                var bodyContent = BodyGenerators.getCurlBody(body, headers);
                if (bodyContent) {
                    body = bodyContent.body;
                    headers = bodyContent.headers;
                }
            }
            if (headers && Object.keys(headers).length > 0) {
                Object.entries(headers).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    lines.push("--header '".concat(key, ": ").concat(value, "'"));
                });
            }
            if (body) {
                if (Array.isArray(body)) {
                    lines.push.apply(lines, body);
                }
                else {
                    lines.push(body);
                }
            }
            return lines.map(function (line, index) { return (index > 0 ? indent(line, 2) : line); }).join(separator);
        },
    },
    {
        id: 'javascript',
        label: 'JavaScript',
        syntax: 'javascript',
        generate: function (_a) {
            var method = _a.method, url = _a.url, headers = _a.headers, body = _a.body;
            var code = '';
            if (body) {
                var lines = BodyGenerators.getJavaScriptBody(body, headers);
                if (lines) {
                    // add the generated code to the top
                    code += lines.code;
                    body = lines.body;
                    headers = lines.headers;
                }
            }
            code += "const response = await fetch('".concat(url, "', {\n    method: '").concat(method.toUpperCase(), "',\n");
            if (headers && Object.keys(headers).length > 0) {
                code += indent("headers: ".concat(stringifyOpenAPI(headers, null, 2), ",\n"), 4);
            }
            if (body) {
                code += indent("body: ".concat(body, "\n"), 4);
            }
            code += '});\n\n';
            code += 'const data = await response.json();';
            return code;
        },
    },
    {
        id: 'python',
        label: 'Python',
        syntax: 'python',
        generate: function (_a) {
            var method = _a.method, url = _a.url, headers = _a.headers, body = _a.body;
            var code = 'import requests\n\n';
            if (body) {
                var lines = BodyGenerators.getPythonBody(body, headers);
                // add the generated code to the top
                if (lines) {
                    code += lines.code;
                    body = lines.body;
                    headers = lines.headers;
                }
            }
            code += "response = requests.".concat(method.toLowerCase(), "(\n");
            code += indent("\"".concat(url, "\",\n"), 4);
            if (headers && Object.keys(headers).length > 0) {
                code += indent("headers=".concat(stringifyOpenAPI(headers), ",\n"), 4);
            }
            var contentType = headers === null || headers === void 0 ? void 0 : headers['Content-Type'];
            if (body) {
                if (body === 'files') {
                    code += indent("files=".concat(body, "\n"), 4);
                }
                else if (isJSON(contentType)) {
                    code += indent("data=json.dumps(".concat(body, ")\n"), 4);
                }
                else {
                    code += indent("data=".concat(body, "\n"), 4);
                }
            }
            code += ')\n\n';
            code += 'data = response.json()';
            return code;
        },
    },
];
function indent(code, spaces) {
    var indent = ' '.repeat(spaces);
    return code
        .split('\n')
        .map(function (line) { return (line ? indent + line : ''); })
        .join('\n');
}
export function parseHostAndPath(url) {
    try {
        var urlObj = new URL(url);
        var path = urlObj.pathname || '/';
        return { host: urlObj.host, path: path };
    }
    catch (_e) {
        // If the URL was invalid do our best to parse the URL.
        // Check for the protocol part and pull it off to grab the host
        var splitted = url.split('//');
        var fullUrl = splitted[1] ? splitted[1] : url;
        // separate paths from the first element (host)
        var parts = fullUrl.split('/');
        // pull off the host (mutates)
        var host = parts.shift();
        // add a leading slash and join the paths again
        var path = "/".concat(parts.join('/'));
        return { host: host, path: path };
    }
}
// Body Generators
var BodyGenerators = {
    getCurlBody: function (body, headers) {
        if (!body || !headers)
            return undefined;
        // Copy headers to avoid mutating the original object
        var headersCopy = __assign({}, headers);
        var contentType = headersCopy['Content-Type'] || '';
        if (isFormData(contentType)) {
            body = isPlainObject(body)
                ? Object.entries(body).map(function (_a) {
                    var key = _a[0], value = _a[1];
                    return "--form '".concat(key, "=").concat(String(value), "'");
                })
                : "--form 'file=@".concat(body, "'");
        }
        else if (isFormUrlEncoded(contentType)) {
            body = isPlainObject(body)
                ? "--data '".concat(Object.entries(body)
                    .map(function (_a) {
                    var key = _a[0], value = _a[1];
                    return "".concat(key, "=").concat(String(value));
                })
                    .join('&'), "'")
                : String(body);
        }
        else if (isText(contentType)) {
            body = "--data '".concat(String(body).replace(/"/g, ''), "'");
        }
        else if (isXML(contentType)) {
            // Convert to XML and ensure proper formatting
            body = "--data-binary $'".concat(convertBodyToXML(body), "'");
        }
        else if (isCSV(contentType)) {
            // We use --data-binary to avoid cURL converting newlines to \r\n
            body = "--data-binary $'".concat(stringifyOpenAPI(body).replace(/"/g, '').replace(/\\n/g, '\n'), "'");
        }
        else if (isGraphQL(contentType)) {
            body = "--data '".concat(stringifyOpenAPI(body), "'");
            // Set Content-Type to application/json for GraphQL, recommended by GraphQL spec
            headersCopy['Content-Type'] = 'application/json';
        }
        else if (isPDF(contentType)) {
            // We use --data-binary to avoid cURL converting newlines to \r\n
            body = "--data-binary '@".concat(String(body), "'");
        }
        else {
            body = "--data '".concat(stringifyOpenAPI(body, null, 2).replace(/\\n/g, '\n'), "'");
        }
        return {
            body: body,
            headers: headersCopy,
        };
    },
    getJavaScriptBody: function (body, headers) {
        if (!body || !headers)
            return;
        var code = '';
        // Copy headers to avoid mutating the original object
        var headersCopy = __assign({}, headers);
        var contentType = headersCopy['Content-Type'] || '';
        // Use FormData for file uploads
        if (isFormData(contentType)) {
            code += 'const formData = new FormData();\n\n';
            if (isPlainObject(body)) {
                Object.entries(body).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    code += "formData.append(\"".concat(key, "\", \"").concat(String(value), "\");\n");
                });
            }
            else if (typeof body === 'string') {
                code += "formData.append(\"file\", \"".concat(body, "\");\n");
            }
            code += '\n';
            body = 'formData';
        }
        else if (isFormUrlEncoded(contentType)) {
            // Use URLSearchParams for form-urlencoded data
            code += 'const params = new URLSearchParams();\n\n';
            if (isPlainObject(body)) {
                Object.entries(body).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    code += "params.append(\"".concat(key, "\", \"").concat(String(value), "\");\n");
                });
            }
            code += '\n';
            body = 'params.toString()';
        }
        else if (isGraphQL(contentType)) {
            if (isPlainObject(body)) {
                Object.entries(body).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    code += "const ".concat(key, " = `\n").concat(indent(String(value), 4), "`;\n\n");
                });
                body = "JSON.stringify({ ".concat(Object.keys(body).join(', '), " })");
                // Set Content-Type to application/json for GraphQL, recommended by GraphQL spec
                headersCopy['Content-Type'] = 'application/json';
            }
            else {
                code += "const query = `\n".concat(indent(String(body), 4), "`;\n\n");
                body = 'JSON.stringify(query)';
            }
        }
        else if (isCSV(contentType)) {
            code += 'const csv = `\n';
            code += indent(String(body), 4);
            code += '`;\n\n';
            body = 'csv';
        }
        else if (isPDF(contentType)) {
            // Use FormData to upload PDF files
            code += 'const formData = new FormData();\n\n';
            code += "formData.append(\"file\", \"".concat(body, "\");\n\n");
            body = 'formData';
        }
        else if (isXML(contentType)) {
            code += 'const xml = `\n';
            // Convert JSON to XML if needed
            code += indent(convertBodyToXML(body), 4);
            code += '`;\n\n';
            body = 'xml';
        }
        else if (isText(contentType)) {
            body = stringifyOpenAPI(body, null, 2);
        }
        else {
            body = "JSON.stringify(".concat(stringifyOpenAPI(body, null, 2), ")");
        }
        return { body: body, code: code, headers: headersCopy };
    },
    getPythonBody: function (body, headers) {
        if (!body || !headers)
            return;
        var code = '';
        var contentType = headers['Content-Type'] || '';
        if (isFormData(contentType)) {
            code += 'files = {\n';
            if (isPlainObject(body)) {
                Object.entries(body).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    code += "".concat(indent("\"".concat(key, "\": \"").concat(String(value), "\","), 4), "\n");
                });
            }
            code += '}\n\n';
            body = 'files';
        }
        else if (isPDF(contentType)) {
            code += 'files = {\n';
            code += "".concat(indent("\"file\": \"".concat(body, "\","), 4), "\n");
            code += '}\n\n';
            body = 'files';
        }
        else if (isXML(contentType)) {
            // Convert JSON to XML if needed
            body = JSON.stringify(convertBodyToXML(body));
        }
        else {
            body = stringifyOpenAPI(body, function (_key, value) {
                switch (value) {
                    case true:
                        return '$$__TRUE__$$';
                    case false:
                        return '$$__FALSE__$$';
                    default:
                        return value;
                }
            })
                .replaceAll('"$$__TRUE__$$"', 'True')
                .replaceAll('"$$__FALSE__$$"', 'False');
        }
        return { body: body, code: code, headers: headers };
    },
    getHTTPBody: function (body, headers) {
        if (!body || !headers)
            return undefined;
        var contentType = headers['Content-Type'] || '';
        var typeHandlers = {
            pdf: function () { return "".concat(stringifyOpenAPI(body, null, 2)); },
            formUrlEncoded: function () {
                var encoded = isPlainObject(body)
                    ? Object.entries(body)
                        .map(function (_a) {
                        var key = _a[0], value = _a[1];
                        return "".concat(key, "=").concat(stringifyOpenAPI(value));
                    })
                        .join('&')
                    : stringifyOpenAPI(body);
                return "\"".concat(encoded.replace(/"/g, "'"), "\"");
            },
            text: function () { return "\"".concat(String(body), "\""); },
            xml: function () {
                // Convert JSON to XML if needed
                return "\"".concat(convertBodyToXML(body), "\"");
            },
            csv: function () { return "\"".concat(stringifyOpenAPI(body).replace(/"/g, ''), "\""); },
            default: function () { return "".concat(stringifyOpenAPI(body, null, 2)); },
        };
        if (isPDF(contentType))
            return typeHandlers.pdf();
        if (isFormUrlEncoded(contentType))
            return typeHandlers.formUrlEncoded();
        if (isText(contentType))
            return typeHandlers.text();
        if (isXML(contentType))
            return typeHandlers.xml();
        if (isCSV(contentType))
            return typeHandlers.csv();
        return typeHandlers.default();
    },
};
/**
 * Converts a body to XML format
 */
function convertBodyToXML(body) {
    // If body is already a string and looks like XML, return it as is
    if (typeof body === 'string' && body.trim().startsWith('<')) {
        return body;
    }
    // If body is not an object, try to parse it as JSON
    if (typeof body !== 'object' || body === null) {
        try {
            body = JSON.parse(body);
        }
        catch (_a) {
            // If parsing fails, return the original body
            return body;
        }
    }
    return json2xml(body).replace(/"/g, '').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}
